import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Wallet } from '@saleshandy/icons';
import { Button } from '@saleshandy/design-system';
import { useTranslation } from 'react-i18next';
import StripeCheckout from 'react-stripe-checkout';
import * as Sentry from '@sentry/react';

import type { IProps } from './purchase-credits-modal-container';

import Modal from '../../../../../../shared/design-system/ui/modal';
import EvCreditPlans from '../ev-credits/components/ev-credit-plans';
import Checkbox from '../../../../../../shared/design-system/components/atoms/checkbox';

import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Images } from '../../../../../../shared/app-constants';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';
import { AnalyticsEvents } from '../../../../../../shared/enums/analytics';
import { contactAdmin } from '../../../../../../shared/utils/contact-admin';
import {
  executeOnRequestStatus,
  getIsRequestPending,
  redirectWithRefresh,
} from '../../../../../../shared/utils';
import { getAgencyConfigDetails } from '../../../../../../shared/utils/user-details';
import GlobalRestrictionModal from '../../../../../home/components/global-restriction-modal';
import {
  PurchaseCreditsRequest,
  PurchaseCreditsType,
} from '../../types/purchase-credits';
import {
  CreditPurchaseOptions,
  generatePurchaseCreditsPlans,
} from '../../utils/helper';
import usePaddleIntegration from '../../../../../../shared/hooks/script-integrations/usePaddleIntegration';

const typeToPurchaseCreditsType: Record<
  'email-verification' | 'lead-finder' | 'ai-credits',
  PurchaseCreditsType
> = {
  'email-verification': PurchaseCreditsType.EV_CREDITS,
  'lead-finder': PurchaseCreditsType.LF_CREDITS,
  'ai-credits': PurchaseCreditsType.AI_CREDITS,
};

const PurchaseCreditsModal: React.FC<IProps> = ({
  userEmail,
  trackingId,
  show,
  evCreditPlans = [],
  onBuyCredits,
  onClose,
  isPurchaseEmailVerificationCreditsLoading,
  remainingCredit,
  sequenceName,
  currentPlan,
  getEmailVerificationPlanResponse,
  sendGetEmailVerificationPlanRequest,
  getEmailVerificationPlanRequestStatus,
  type,
  sendGetLeadFinderOneTimePlansRequest,
  getLeadFinderOneTimePlansRequestStatus,
  getLeadFinderOneTimePlansResponse,
  openTalkToSalesChat,
  isDemoAccount,
  showPlansLoading = false,
  paymentProvider,
  sendPurchaseAiCreditsRequest,
  purchaseAiCreditsRequestStatus,
  purchaseAiCreditsRequestResponse,
  calculateCreditsPricesRequestStatus,
  calculateCreditsPricesResponse,
  purchaseCreditsRequestStatus,
  purchaseCreditsResponse,
  sendCalculateCreditsPricesRequest,
  sendPurchaseCreditsRequest,
  resetCalculateCreditsPricesRequest,
  resetPurchaseCreditsRequest,
  onCheckoutComplete,
  onPaymentFailed,
  paddleCustomerId,
}) => {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState(
    () =>
      (evCreditPlans?.length > 0 &&
        evCreditPlans.find((plan) => plan.isBestValue === 1)) ||
      null,
  );
  const [isChecked, setIsChecked] = useState<boolean>(false);

  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  // Use ref instead of state to avoid stale closure issues in callbacks
  const currentOrderIdRef = useRef<string | undefined>();

  const isCheckoutOpenRef = useRef<boolean>(false);

  const checkoutHandledRef = useRef<boolean>(false);

  const {
    isReady: isPaddleReady,
    openCheckout,
    prepareCheckoutWindow,
  } = usePaddleIntegration({
    onCheckoutComplete: () => {
      if (checkoutHandledRef.current) {
        return;
      }

      checkoutHandledRef.current = true;
      isCheckoutOpenRef.current = false;

      if (onCheckoutComplete) {
        onCheckoutComplete(currentOrderIdRef.current);
      }

      setTimeout(() => {
        if (window.Paddle?.Checkout?.close) {
          window.Paddle.Checkout.close();
        }

        resetPurchaseCreditsRequest();
        onClose();
      }, 100);
    },
    onCheckoutError: () => {
      checkoutHandledRef.current = true;
      isCheckoutOpenRef.current = false;

      resetPurchaseCreditsRequest();
      onClose();
      onPaymentFailed?.();
    },
    onError: () => {
      // Paddle integration error - silent fail
    },
    onCheckoutClosed: () => {
      if (!checkoutHandledRef.current && show) {
        isCheckoutOpenRef.current = false;

        setTimeout(() => {
          resetPurchaseCreditsRequest();
          onClose();
        }, 50);
      }
    },
  });

  const purchaseCreditsType = typeToPurchaseCreditsType[type];

  const handleCheckBox = () => {
    setIsChecked((preState) => !preState);
    (document.activeElement as HTMLElement)?.blur();
  };

  const onSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const onPurchaseHandler = (token) => {
    onBuyCredits(token.id, +selectedPlan.id);
  };

  // Handle analytics tracking
  const trackPayNowEvent = () => {
    window.analytics?.track?.({
      userId: trackingId,
      event: AnalyticsEvents.ClickedOnPayNow,
      properties: {
        Source: 'Verification Credits',
        current_plan: currentPlan,
      },
    });
  };

  const contactAdminHandler = () => {
    const generalEmailSub = t('messages.general_email_sub_for_purchase_ev');
    const emailSubForSequenceProspectVerify = t(
      'messages.email_sub_for_purchase_ev_from_sequence_settings',
    );
    const generalEmailBody =
      `Hey, %0D%0A %0D%0A I wish to buy ${selectedPlan.name} for Email Verification from SalesHandy.` +
      `%0D%0A Please make arrangements for the same by given URL.` +
      `%0D%0A ${process.env.REACT_APP_V3_PURCHASE_EV_CREDIT_URL} %0D%0A %0D%0AThanks in advance`;
    const sequenceProspectEmailBody =
      `To verify the prospects of our lates sequence "${sequenceName}" we need to purchase credits.` +
      `%0D%0A %0D%0APlease purchase credits to ensure that the sequence "${sequenceName}" continues smoothly.`;

    contactAdmin(
      sequenceName ? emailSubForSequenceProspectVerify : generalEmailSub,
      sequenceName ? sequenceProspectEmailBody : generalEmailBody,
    );
  };

  // Default to Stripe when paymentProvider is null/undefined
  const shouldUseStripeCheckout = paymentProvider !== 'paddle';
  const shouldUseAiCreditsRedirect =
    paymentProvider !== 'paddle' && type === 'ai-credits';

  // Handle purchase logic
  const handlePurchase = () => {
    if (shouldUseAiCreditsRedirect) {
      sendPurchaseAiCreditsRequest({
        creditCount: selectedPlan?.credits,
        successUrl: `${window.location.origin}/billing/subscriptions/ai-credits?message=success`,
        cancelUrl: `${window.location.origin}/billing/subscriptions/ai-credits?message=failed`,
        paymentProvider,
      });
      trackPayNowEvent();
      return;
    }
    if (shouldUseStripeCheckout) {
      // Stripe EV/LF: token flow via onBuyCredits handles API call
      trackPayNowEvent();
      return;
    }

    // White-label Paddle: pre-open window to avoid popup blocker
    if (getAgencyConfigDetails()?.clientBaseUrl) {
      prepareCheckoutWindow();
    }

    const payload: PurchaseCreditsRequest = {
      creditCount: selectedPlan?.credits,
      type: purchaseCreditsType,
      currencyCode: 'USD',
      paymentProvider,
    };
    sendPurchaseCreditsRequest(payload);
    trackPayNowEvent();
  };

  useEffect(() => {
    executeOnRequestStatus({
      status: purchaseAiCreditsRequestStatus,
      onSuccess: () => {
        const url = purchaseAiCreditsRequestResponse?.data?.url || null;
        if (url) {
          redirectWithRefresh(url);
        } else {
          redirectWithRefresh(
            `${window.location.origin}/billing/subscriptions/ai-credits?message=no-url-found`,
          );
        }
      },
      onFailed: () => {
        redirectWithRefresh(
          `${window.location.origin}/billing/subscriptions/ai-credits?message=failed`,
        );
      },
    });
  }, [purchaseAiCreditsRequestStatus]);

  useEffect(() => {
    if (show && shouldUseStripeCheckout && !window?.Stripe) {
      Sentry.captureMessage('Stripe Script is not loaded', 'warning');
      redirectWithRefresh('/billing/subscriptions/lead-finder');
    }
  }, [show, shouldUseStripeCheckout]);

  useEffect(() => {
    if (!show || paymentProvider === 'paddle') return;
    if (
      type === 'email-verification' &&
      !getEmailVerificationPlanResponse?.length
    ) {
      sendGetEmailVerificationPlanRequest();
    } else if (
      type === 'lead-finder' &&
      !getLeadFinderOneTimePlansResponse?.length
    ) {
      sendGetLeadFinderOneTimePlansRequest();
    }
  }, [
    show,
    paymentProvider,
    type,
    getEmailVerificationPlanResponse?.length,
    getLeadFinderOneTimePlansResponse?.length,
  ]);

  useEffect(() => {
    if (paymentProvider === 'paddle') return;
    executeOnRequestStatus({
      status: getEmailVerificationPlanRequestStatus,
      onSuccess: () => {
        setSelectedPlan(
          () =>
            (getEmailVerificationPlanResponse?.length > 0 &&
              getEmailVerificationPlanResponse.find(
                (plan) => plan.isBestValue === 1,
              )) ||
            null,
        );
      },
    });
  }, [
    paymentProvider,
    getEmailVerificationPlanRequestStatus,
    getEmailVerificationPlanResponse,
  ]);

  useEffect(() => {
    if (paymentProvider === 'paddle') return;
    executeOnRequestStatus({
      status: getLeadFinderOneTimePlansRequestStatus,
      onSuccess: () => {
        setSelectedPlan(
          () =>
            (getLeadFinderOneTimePlansResponse?.length > 0 &&
              getLeadFinderOneTimePlansResponse.find(
                (plan) => plan.isBestValue === 1,
              )) ||
            null,
        );
      },
    });
  }, [
    paymentProvider,
    getLeadFinderOneTimePlansRequestStatus,
    getLeadFinderOneTimePlansResponse,
  ]);

  useEffect(() => {
    if (paymentProvider !== 'paddle') return;
    executeOnRequestStatus({
      status: calculateCreditsPricesRequestStatus,
      onSuccess: () => {
        const plans = generatePurchaseCreditsPlans(
          calculateCreditsPricesResponse,
          purchaseCreditsType,
        );
        const bestValue =
          plans?.length > 0 && plans.find((plan) => plan.isBestValue === 1);
        setSelectedPlan(bestValue || plans?.[0] || null);
      },
    });
  }, [
    paymentProvider,
    calculateCreditsPricesRequestStatus,
    calculateCreditsPricesResponse,
    purchaseCreditsType,
  ]);

  const creditsToUse = () => {
    if (paymentProvider === 'paddle') {
      return generatePurchaseCreditsPlans(
        calculateCreditsPricesResponse,
        purchaseCreditsType,
      );
    }
    if (evCreditPlans?.length > 0) {
      return evCreditPlans;
    }
    if (type === 'email-verification') {
      return getEmailVerificationPlanResponse;
    }
    return getLeadFinderOneTimePlansResponse;
  };

  // Handle Paddle checkout opening when purchase request succeeds (Paddle users only)
  useEffect(() => {
    if (paymentProvider !== 'paddle') return;
    executeOnRequestStatus({
      status: purchaseCreditsRequestStatus,
      onSuccess: () => {
        if (isCheckoutOpenRef.current) {
          return;
        }

        if (!isPaddleReady) {
          onClose();
          resetPurchaseCreditsRequest();
          return;
        }

        const extractedOrderId = purchaseCreditsResponse?.transaction?.orderId;
        currentOrderIdRef.current = extractedOrderId;

        isCheckoutOpenRef.current = true;
        checkoutHandledRef.current = false;

        openCheckout({
          transactionId: purchaseCreditsResponse?.transaction?.transactionId,
          ...(!paddleCustomerId && { customer: { email: userEmail } }),
          settings: {
            displayMode: 'overlay',
            theme: 'light',
            ...(paddleCustomerId && { allowLogout: false }),
          },
        });
      },
      onFailed: () => {
        onClose();
        resetPurchaseCreditsRequest();
      },
    });
  }, [
    paymentProvider,
    purchaseCreditsRequestStatus,
    isPaddleReady,
    openCheckout,
    purchaseCreditsResponse,
    userEmail,
    onClose,
    resetPurchaseCreditsRequest,
  ]);

  useEffect(() => {
    if (show && type) {
      currentOrderIdRef.current = undefined;
      isCheckoutOpenRef.current = false;
      checkoutHandledRef.current = false;

      if (paymentProvider === 'paddle') {
        sendCalculateCreditsPricesRequest({
          creditCounts: CreditPurchaseOptions[purchaseCreditsType],
          type: purchaseCreditsType,
          currencyCode: 'USD',
        });
      }
    }

    return () => {
      currentOrderIdRef.current = undefined;
      resetCalculateCreditsPricesRequest();
      resetPurchaseCreditsRequest();
    };
  }, [show, type, paymentProvider, purchaseCreditsType]);

  const labelText = useMemo(() => {
    switch (type) {
      case 'email-verification':
        return t('messages.purchase_ev_credits_agreement');
      case 'ai-credits':
        return t('messages.ai_credits_agreement');
      case 'lead-finder':
        return t('messages.purchase_lead_finder_credits_agreement');
      default:
        return '';
    }
  }, [type]);

  const showTalkToSalesButton =
    hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ) &&
    !isDemoAccount &&
    purchaseCreditsType === PurchaseCreditsType.LF_CREDITS &&
    selectedPlan?.amount > 1000;

  const renderCheckoutButton = () => {
    if (shouldUseAiCreditsRedirect) {
      return (
        <Button
          onClick={() => handlePurchase()}
          isLoading={isPurchaseEmailVerificationCreditsLoading}
          disabled={
            (type === 'ai-credits' &&
              getIsRequestPending(purchaseAiCreditsRequestStatus)) ||
            !selectedPlan ||
            isPurchaseEmailVerificationCreditsLoading ||
            !isChecked
          }
          className="buy-now-btn"
          loadingText="Buying..."
        >
          {t('labels.buy_now')}
        </Button>
      );
    }
    if (shouldUseStripeCheckout && !shouldUseAiCreditsRedirect) {
      return (
        <StripeCheckout
          token={onPurchaseHandler}
          name="Saleshandy"
          image={Images.SHIcon70}
          stripeKey={process.env.REACT_APP_CFG_PAY_01}
          amount={selectedPlan?.amount * 100}
          zipCode
          billingAddress
          ComponentClass="div"
        >
          <Button
            onClick={() => handlePurchase()}
            isLoading={isPurchaseEmailVerificationCreditsLoading}
            disabled={
              (type === 'email-verification' &&
                getIsRequestPending(getEmailVerificationPlanRequestStatus)) ||
              (type === 'lead-finder' &&
                getIsRequestPending(getLeadFinderOneTimePlansRequestStatus)) ||
              !selectedPlan ||
              isPurchaseEmailVerificationCreditsLoading ||
              !isChecked
            }
            className="buy-now-btn"
            loadingText="Buying..."
          >
            {t('labels.buy_now')}
          </Button>
        </StripeCheckout>
      );
    }
    return (
      <Button
        onClick={handlePurchase}
        isLoading={getIsRequestPending(purchaseCreditsRequestStatus)}
        disabled={
          !isPaddleReady ||
          getIsRequestPending(purchaseCreditsRequestStatus) ||
          getIsRequestPending(calculateCreditsPricesRequestStatus) ||
          !selectedPlan ||
          !isChecked
        }
        className="buy-now-btn"
        loadingText="Buying..."
      >
        {t('labels.buy_now')}
      </Button>
    );
  };

  return (
    <>
      <Modal
        show={show}
        title={
          <div className="purchase-credits-modal--custom-header">
            <span>
              <Wallet />
            </span>
            <span>{t('labels.purchase_credits')}</span>
          </div>
        }
        className="purchase-credits-modal"
        variant="border"
        showCloseButton
        onHide={onClose}
        hideFooter
      >
        <div className="purchase-credits-modal--plans">
          <EvCreditPlans
            credits={creditsToUse()}
            onSelectPlan={(plan) => onSelectPlan(plan)}
            selectedPlan={selectedPlan}
            remainingCredit={remainingCredit}
            isPlansLoading={
              (paymentProvider === 'paddle' &&
                getIsRequestPending(calculateCreditsPricesRequestStatus)) ||
              (paymentProvider !== 'paddle' &&
                (showPlansLoading ||
                  (type === 'email-verification' &&
                    getIsRequestPending(
                      getEmailVerificationPlanRequestStatus,
                    )) ||
                  (type === 'lead-finder' &&
                    getIsRequestPending(
                      getLeadFinderOneTimePlansRequestStatus,
                    ))))
            }
          />

          {hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ) && (
            <Checkbox
              disabled={false}
              label={labelText}
              intermediate={false}
              checked={isChecked}
              onChange={handleCheckBox}
              className="purchase-ev-credits-agreement"
            />
          )}
        </div>

        <div className="purchase-credits-modal--custom-footer">
          <p className="selected-plan-amount">
            {selectedPlan ? `$${selectedPlan?.amount}` : ''}
          </p>

          {isDemoAccount && (
            <Button
              className="contact-admin-btn"
              onClick={() => setShowGlobalRestrictionModal(true)}
              disabled={!isChecked}
            >
              Buy Now
            </Button>
          )}

          {!hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ) &&
            !isDemoAccount && (
              <Button
                className="contact-admin-btn"
                onClick={contactAdminHandler}
              >
                {t('labels.contact_admin')}
              </Button>
            )}
          {showTalkToSalesButton && (
            <Button
              className="contact-admin-btn"
              onClick={openTalkToSalesChat}
              disabled={!isChecked}
            >
              Talk to Sales
            </Button>
          )}
          {hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ) &&
            !isDemoAccount &&
            !showTalkToSalesButton &&
            renderCheckoutButton()}
        </div>
      </Modal>

      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Purchasing credit is disabled in demo mode"
        content="This is a demo account with sample data. Signup to purchase credits."
        primaryBtnLabel="Sign up"
        redirectTo={`billing/subscriptions/${
          type === 'lead-finder' ? 'lead-finder' : 'ev-credits'
        }`}
      />
    </>
  );
};

export default PurchaseCreditsModal;
